import React, { useState, useEffect } from 'react';
import {
    Box, Button, Stack, Typography, ToggleButton, ToggleButtonGroup, Chip, Divider,
    FormControlLabel, Checkbox, Radio, RadioGroup, Alert, CircularProgress, Tooltip,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import RestoreIcon from '@mui/icons-material/Restore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import MUIModal from '../../components/MuiModal';
import { apiConstants, get, post } from '../../common/constants';
import { useSnackbar } from 'notistack';
import { getToken } from '../../helpers/ReduxHelpers';
import jwt_decode from 'jwt-decode';
import { useScanViewer, ScanModal } from './orderScanViewer';

// ── helpers ──────────────────────────────────────────────────────────────────
const fmtTs = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts);
    if (isNaN(d.getTime())) return String(ts);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });
};
const label = (e) => e.statusEnum || (e.statusCode != null ? `Status ${e.statusCode}` : '—');

const currentUser = () => {
    try {
        const d = jwt_decode(getToken()) || {};
        return d.sub || d.email || d.user_name || d.userName || d.username || undefined;
    } catch (e) { return undefined; }
};

// Backend supplies the tag per rebuilt entry (UNCHANGED/DUPLICATE/REMAPPED/NEW). Styling per bucket.
const TAG_STYLE = {
    NEW: { color: '#166534', bg: '#dcfce7', border: '#86efac', chip: 'NEW' },
    REMAPPED: { color: '#92400e', bg: '#fef3c7', border: '#fcd34d', chip: 'REMAPPED' },
    DUPLICATE: { color: '#475569', bg: '#f1f5f9', border: '#e2e8f0', chip: 'DUP' },
    UNCHANGED: { color: '#475569', bg: 'transparent', border: '#e2e8f0', chip: '' },
};

// ── one timeline row (clickable → its raw scan) ───────────────────────────────
function TimelineRow({ e, side, onClick }) {
    const st = side === 'rebuilt' ? (TAG_STYLE[e.tag] || TAG_STYLE.UNCHANGED) : TAG_STYLE.UNCHANGED;
    const clickable = side === 'current' ? true : !!e.scan;
    return (
        <Box
            onClick={clickable ? onClick : undefined}
            title={clickable ? 'View raw scan' : ''}
            sx={{
                border: '1px solid', borderColor: st.border, bgcolor: st.bg,
                borderRadius: '8px', px: 1.25, py: 0.75,
                cursor: clickable ? 'pointer' : 'default',
                transition: 'background .12s, border-color .12s',
                '&:hover': clickable ? { borderColor: st.color, bgcolor: st.bg === 'transparent' ? '#f8fafc' : st.bg } : {},
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: st.color, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {label(e)}
                </Typography>
                {side === 'rebuilt' && e.willNotify && (
                    <Tooltip title="A client webhook will fire for this status">
                        <NotificationsActiveIcon sx={{ fontSize: 15, color: '#1d4ed8' }} />
                    </Tooltip>
                )}
                {side === 'rebuilt' && !e.willNotify && (e.tag === 'NEW' || e.tag === 'REMAPPED' || e.tag === 'DUPLICATE') && (
                    <Tooltip title="Silent — no client notification">
                        <NotificationsOffOutlinedIcon sx={{ fontSize: 15, color: '#94a3b8' }} />
                    </Tooltip>
                )}
                {side === 'rebuilt' && st.chip && (
                    <Chip label={st.chip} size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700, color: st.color, bgcolor: '#fff', border: `1px solid ${st.border}` }} />
                )}
            </Stack>
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                {fmtTs(e.timestamp)}{e.location ? ` · ${e.location}` : ''}
            </Typography>
            {e.remark && (
                <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.remark}</Typography>
            )}
        </Box>
    );
}

function TimelineColumn({ title, entries, side, onRowClick }) {
    return (
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'text.secondary', mb: 1 }}>
                {title} <Box component="span" sx={{ color: 'text.disabled', fontWeight: 500 }}>· {entries?.length || 0}</Box>
            </Typography>
            <Box sx={{ maxHeight: 360, overflowY: 'auto', pr: 0.5 }}>
                {(!entries || entries.length === 0) && (
                    <Typography sx={{ fontSize: 13, color: 'text.disabled', py: 2, textAlign: 'center' }}>No entries</Typography>
                )}
                <Stack spacing={0.75}>
                    {(entries || []).map((e, i) => <TimelineRow key={i} e={e} side={side} onClick={() => onRowClick(e)} />)}
                </Stack>
            </Box>
        </Box>
    );
}

function SummaryChips({ s }) {
    if (!s) return null;
    const item = (lbl, val, color) => (
        <Chip label={`${lbl}: ${val ?? 0}`} size="small" sx={{ fontWeight: 600, fontSize: 12, color, bgcolor: `${color}14`, border: `1px solid ${color}40` }} />
    );
    return (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            {item('Current', s.currentCount, '#475569')}
            {item('Rebuilt', s.rebuiltCount, '#0b6e72')}
            {item('New', s.added, '#166534')}
            {item('Remapped', s.recalculated, '#92400e')}
            {item('Duplicate', s.duplicate, '#64748b')}
            {item('Unchanged', s.unchanged, '#94a3b8')}
            {item('Will notify clients', s.willNotifyClient, '#1d4ed8')}
        </Stack>
    );
}

// ── main modal ───────────────────────────────────────────────────────────────
export default function ResyncActionModal({ open, onClose, awb, reference, onDone, manualLocked = false, manuallyUpdatedBy }) {
    const { enqueueSnackbar } = useSnackbar();
    const scanViewer = useScanViewer(awb);
    const [action, setAction] = useState('RESYNC');
    const [busy, setBusy] = useState(false);

    // delete-and-resync
    const [preview, setPreview] = useState(null);
    const [applyResult, setApplyResult] = useState(null);
    const [sendClient, setSendClient] = useState(true);
    const [sendCustomer, setSendCustomer] = useState(false);
    const [force, setForce] = useState(false);

    // restore
    const [backups, setBackups] = useState(null);
    const [selectedBackup, setSelectedBackup] = useState('');

    useEffect(() => {
        if (open) {
            // A manually-updated order can't be resynced (a courier-scan rebuild would discard the manual
            // entry). Land on Restore — the only action still available — instead of a disabled tab.
            setAction(manualLocked ? 'RESTORE_INITIAL' : 'RESYNC'); setBusy(false);
            setPreview(null); setApplyResult(null); setSendClient(true); setSendCustomer(false); setForce(false);
            setBackups(null); setSelectedBackup('');
        }
    }, [open, awb, manualLocked]);

    const shrinking = preview?.summary && preview.summary.rebuiltCount < preview.summary.currentCount;

    // Manual-status lock. Known up front from the order-detail timeline (prop); on the list pages the
    // order_history isn't loaded, so we also derive it from the preview summary / a blocked apply. The
    // backend enforces the same rule independently — this only pre-disables the UI + explains why.
    const locked = manualLocked
        || preview?.summary?.manualStatusUpdate === true
        || applyResult?.status === 'SKIPPED_MANUAL_UPDATE';

    // click a CURRENT row → fetch its order_events scan (statusCode + eventTime); click a REBUILT row →
    // show its inline scan (already in the preview payload).
    const openCurrentScan = (e) => scanViewer.showItemScan({ orderStatusCode: e.statusCode, timestamp: e.timestamp, orderStatusDescription: e.statusEnum });
    const openRebuiltScan = (e) => { if (e.scan) scanViewer.showInlineScans(e.statusEnum || 'Scan detail', `AWB ${awb} · code ${e.statusCode} · ${fmtTs(e.timestamp)}`, [e.scan]); };

    // ── plain resync ──
    const runResync = () => {
        if (locked) return;
        setBusy(true);
        const params = { waybill: awb };
        if (reference) params.reference = reference;
        get(apiConstants.RESYNC_ORDER, params)
            .then((res) => enqueueSnackbar(res.status === 200 ? 'AWB queued for resync' : 'Resync not accepted', { variant: res.status === 200 ? 'success' : 'error' }))
            .catch((e) => enqueueSnackbar(e?.response?.data?.message || 'Resync failed', { variant: 'error' }))
            .finally(() => setBusy(false));
    };

    // ── delete-and-resync ──
    const runPreview = () => {
        if (locked) return;
        setBusy(true); setApplyResult(null);
        post(apiConstants.RESYNC_PREVIEW, { awb, reference })
            .then((res) => {
                const d = res.data;
                if (d?.status === 'PREVIEW') setPreview(d);
                else { setPreview(null); enqueueSnackbar(prettyStatus(d), { variant: d?.status === 'NO_PCIS_DATA' ? 'warning' : 'error' }); }
            })
            .catch((e) => enqueueSnackbar(e?.response?.data?.message || 'Preview failed', { variant: 'error' }))
            .finally(() => setBusy(false));
    };

    const runApply = () => {
        if (locked) return;
        setBusy(true);
        post(apiConstants.RESYNC_APPLY, {
            awb, reference, force,
            sendClientWebhooks: sendClient,
            sendCustomerNotifications: sendCustomer,
            triggeredBy: currentUser(),
        })
            .then((res) => {
                const d = res.data;
                setApplyResult(d);
                if (d?.status === 'APPLIED') {
                    enqueueSnackbar(`Applied — ${d.summary?.willNotifyClient ?? 0} client webhook(s) fired. Backup ${d.backupId ? d.backupId.slice(0, 8) : ''}`, { variant: 'success' });
                    if (onDone) onDone();
                } else if (d?.status === 'SKIPPED_GUARDRAIL') {
                    enqueueSnackbar('Guardrail: rebuilt history has fewer scans than current. Tick "Force" to override.', { variant: 'warning' });
                } else {
                    enqueueSnackbar(prettyStatus(d), { variant: 'error' });
                }
            })
            .catch((e) => enqueueSnackbar(e?.response?.data?.message || 'Apply failed', { variant: 'error' }))
            .finally(() => setBusy(false));
    };

    // ── restore ──
    const loadBackups = () => {
        setBusy(true); setBackups(null);
        get(`${apiConstants.RESYNC_BACKUPS}/${encodeURIComponent(awb)}`)
            .then((res) => {
                const list = Array.isArray(res.data) ? res.data : [];
                setBackups(list);
                if (list.length) setSelectedBackup(list[0].backupId);
            })
            .catch(() => setBackups([]))
            .finally(() => setBusy(false));
    };

    const runRestore = () => {
        setBusy(true);
        post(apiConstants.RESYNC_RESTORE, { awb, backupId: selectedBackup || undefined, restoredBy: currentUser() })
            .then((res) => {
                const d = res.data;
                if (d?.status === 'RESTORED') {
                    enqueueSnackbar(`Restored ${d.restoredHistoryCount ?? 0} history entr(y/ies)`, { variant: 'success' });
                    if (onDone) onDone();
                    onClose();
                } else {
                    enqueueSnackbar(d?.message || prettyStatus(d), { variant: d?.status === 'NO_BACKUP' ? 'warning' : 'error' });
                }
            })
            .catch((e) => enqueueSnackbar(e?.response?.data?.message || 'Restore failed', { variant: 'error' }))
            .finally(() => setBusy(false));
    };

    useEffect(() => {
        if (open && action === 'RESTORE_INITIAL' && backups === null && awb) loadBackups();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action, open]);

    const title = (
        <Stack direction="row" alignItems="center" spacing={1.5}>
            <span>Resync</span>
            <Chip label={awb} size="small" sx={{ fontWeight: 700, fontFamily: 'monospace' }} />
            {reference ? <Chip label={reference} size="small" variant="outlined" /> : null}
        </Stack>
    );

    return (
        <MUIModal open={open} handleClose={onClose} title={title} maxWidth="lg" hideClose>
            <Box sx={{ pt: 0.5 }}>
                <ToggleButtonGroup value={action} exclusive size="small" onChange={(e, v) => { if (v) setAction(v); }} sx={{ mb: 2 }}>
                    <ToggleButton value="RESYNC" disabled={locked}><SyncIcon fontSize="small" sx={{ mr: 0.75 }} />Resync</ToggleButton>
                    <ToggleButton value="DELETE_AND_RESYNC" disabled={locked}><AutoModeIcon fontSize="small" sx={{ mr: 0.75 }} />Delete &amp; Resync</ToggleButton>
                    <ToggleButton value="RESTORE_INITIAL"><RestoreIcon fontSize="small" sx={{ mr: 0.75 }} />Restore</ToggleButton>
                </ToggleButtonGroup>

                {/* Manual-status lock — resync & delete-and-resync would discard the hand-applied status. */}
                {locked && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <b>Resync</b> and <b>Delete &amp; Resync</b> are disabled for this order — it has a manually
                        updated status in its history{manuallyUpdatedBy ? ` (by ${manuallyUpdatedBy})` : ''}. Rebuilding
                        from courier scans would discard that manual entry, so only live courier webhooks are applied to
                        this order. Use <b>Restore</b> if you need to revert the history to an earlier snapshot.
                    </Alert>
                )}

                {/* ── RESYNC ── */}
                {action === 'RESYNC' && (
                    <Box>
                        <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 2 }}>
                            Queues this AWB for a normal status resync — fetches the latest scans and appends any missing
                            statuses. Does not wipe or re-map existing history.
                        </Typography>
                        <Button variant="contained" startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />} disabled={busy || locked} onClick={runResync}>
                            Run Resync
                        </Button>
                    </Box>
                )}

                {/* ── DELETE & RESYNC ── */}
                {action === 'DELETE_AND_RESYNC' && (
                    <Box>
                        <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 2 }}>
                            Re-projects the raw courier scans through the <b>current</b> status mapping, replacing this order's
                            history. Preview first — nothing is written until you Apply. Click any row to see its raw scan.
                            Clients are notified only for new or re-mapped statuses; a 7-day backup is kept for restore.
                        </Typography>

                        {!preview && (
                            <Button variant="outlined" disabled={busy || locked} startIcon={busy ? <CircularProgress size={16} /> : <AutoModeIcon />} onClick={runPreview}>
                                Preview changes
                            </Button>
                        )}

                        {preview && (
                            <Box>
                                <SummaryChips s={preview.summary} />
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                                    <TimelineColumn title="Current" entries={preview.current} side="current" onRowClick={openCurrentScan} />
                                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', color: 'text.disabled' }}><ArrowForwardIcon /></Box>
                                    <TimelineColumn title="After resync" entries={preview.rebuilt} side="rebuilt" onRowClick={openRebuiltScan} />
                                </Stack>

                                {shrinking && (
                                    <Alert severity="warning" sx={{ mb: 1.5 }}>
                                        The rebuilt history has <b>fewer</b> scans ({preview.summary.rebuiltCount}) than the current one
                                        ({preview.summary.currentCount}). This can be a courier/PCIS blip. Applying is blocked unless you force it.
                                    </Alert>
                                )}

                                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                                    <FormControlLabel
                                        control={<Checkbox size="small" checked={sendClient} onChange={(e) => setSendClient(e.target.checked)} />}
                                        label={<Typography sx={{ fontSize: 13 }}>Notify clients (webhooks) for new / re-mapped</Typography>}
                                    />
                                    <FormControlLabel
                                        control={<Checkbox size="small" checked={sendCustomer} onChange={(e) => setSendCustomer(e.target.checked)} />}
                                        label={<Typography sx={{ fontSize: 13 }}>Also notify customers (WhatsApp/SMS)</Typography>}
                                    />
                                    {shrinking && (
                                        <FormControlLabel
                                            control={<Checkbox size="small" color="warning" checked={force} onChange={(e) => setForce(e.target.checked)} />}
                                            label={<Typography sx={{ fontSize: 13, color: 'warning.main' }}>Force (override guardrail)</Typography>}
                                        />
                                    )}
                                </Stack>

                                {applyResult && applyResult.status === 'APPLIED' && (
                                    <Alert severity="success" sx={{ mb: 1.5 }}>
                                        Applied. Client webhooks fired: {applyResult.summary?.willNotifyClient ?? 0}. Backup id:{' '}
                                        <b style={{ fontFamily: 'monospace' }}>{applyResult.backupId}</b> (restorable for 7 days).
                                    </Alert>
                                )}

                                <Stack direction="row" spacing={1.5}>
                                    <Button variant="text" disabled={busy} onClick={runPreview}>Re-preview</Button>
                                    <Box sx={{ flex: 1 }} />
                                    <Tooltip title={locked ? 'Disabled — order has a manually updated status' : (shrinking && !force ? 'Tick Force to apply a shrinking rebuild' : '')}>
                                        <span>
                                            <Button
                                                variant="contained" color="primary"
                                                disabled={busy || locked || (shrinking && !force) || applyResult?.status === 'APPLIED'}
                                                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <AutoModeIcon />}
                                                onClick={runApply}
                                            >
                                                Apply delete &amp; resync
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Stack>
                            </Box>
                        )}
                    </Box>
                )}

                {/* ── RESTORE ── */}
                {action === 'RESTORE_INITIAL' && (
                    <Box>
                        <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 2 }}>
                            Reverts this order's history to a pre-resync snapshot. Snapshots are kept for 7 days after a
                            delete-and-resync.
                        </Typography>

                        {busy && backups === null && (
                            <Stack direction="row" alignItems="center" spacing={1}><CircularProgress size={18} /><Typography sx={{ fontSize: 13 }}>Loading backups…</Typography></Stack>
                        )}
                        {backups !== null && backups.length === 0 && (
                            <Alert severity="info">No backup available — none was taken, or the 7-day restore window has elapsed.</Alert>
                        )}
                        {backups !== null && backups.length > 0 && (
                            <Box>
                                <RadioGroup value={selectedBackup} onChange={(e) => setSelectedBackup(e.target.value)}>
                                    <Stack spacing={1} sx={{ maxHeight: 300, overflowY: 'auto' }}>
                                        {backups.map((b) => (
                                            <Box key={b.backupId} sx={{ border: '1px solid', borderColor: selectedBackup === b.backupId ? 'primary.main' : '#e2e8f0', borderRadius: '8px', px: 1.5, py: 1 }}>
                                                <FormControlLabel
                                                    value={b.backupId}
                                                    control={<Radio size="small" />}
                                                    label={
                                                        <Box>
                                                            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                                                                {fmtTs(b.backedUpAt)} · {b.historyCount} entr{b.historyCount === 1 ? 'y' : 'ies'}
                                                                {b.restoredAt ? <Chip label="restored" size="small" sx={{ ml: 1, height: 18, fontSize: 10 }} /> : null}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>
                                                                {b.action || 'DELETE_AND_RESYNC'}{b.triggeredBy ? ` · by ${b.triggeredBy}` : ''}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                            </Box>
                                        ))}
                                    </Stack>
                                </RadioGroup>
                                <Divider sx={{ my: 1.5 }} />
                                <Stack direction="row">
                                    <Box sx={{ flex: 1 }} />
                                    <Button variant="contained" color="warning" disabled={busy || !selectedBackup} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <RestoreIcon />} onClick={runRestore}>
                                        Restore selected
                                    </Button>
                                </Stack>
                            </Box>
                        )}
                    </Box>
                )}

                <Divider sx={{ mt: 2, mb: 1 }} />
                <Stack direction="row" justifyContent="flex-end">
                    <Button onClick={onClose} disabled={busy}>Close</Button>
                </Stack>
            </Box>

            {/* raw-scan viewer shared with the order-detail timeline */}
            <ScanModal {...scanViewer.modalProps} />
        </MUIModal>
    );
}

function prettyStatus(d) {
    if (!d) return 'No response from resync service';
    switch (d.status) {
        case 'NO_PCIS_DATA': return 'No PCIS scan data for this AWB — cannot rebuild history.';
        case 'ORDER_NOT_FOUND': return 'Order not found for this AWB.';
        case 'NO_BACKUP': return 'No backup available (absent or expired).';
        case 'LAMBDA_ERROR': return `Resync service error: ${d.message || 'unknown'}`;
        case 'BAD_REQUEST': return d.message || 'Bad request.';
        default: return d.message || d.status || 'Unexpected response';
    }
}
